import { Box, Center, Container, Flex, SlideFade, Spinner, Text, useToast } from '@chakra-ui/react';
import { Form, Formik, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import Button from '../../../../../components/ui/buttons/Button';
import { DASHBOARD } from '../../../../../constants/urls';
import { useOnceWhen } from '../../../../../hooks';
import {
  trackConditionReportCompleted,
  trackConditionReportStarted,
} from '../../../../../services/analytics/marketplaceEvents';
import { UserDeal } from '../../../utils';
import DashboardBackButton from '../../DashboardBackButton';
import ConditionReportDone from './ConditionReportDone';
import MarketplaceConditionProgress from './MarketplaceConditionProgress';
import StepQuestions from './StepQuestions';
import {
  buildMissingSectionsMessage,
  collectInvalidSectionLabels,
  stepHasUnansweredText,
} from './encodeDecodeAnswers';
import StepShell from './steps/StepShell';
import useMarketplaceConditionReport from './useMarketplaceConditionReport';

const FormContent = ({ form }: { form: ReturnType<typeof useMarketplaceConditionReport> }) => {
  const { values, errors, touched, setTouched, validateForm } =
    useFormikContext<Record<string, unknown>>();
  const currentStep = form.steps[form.currentStep];
  const isLast = form.currentStep === form.steps.length - 1;

  const missingSectionsMessage =
    currentStep && !form.isOnDone
      ? buildMissingSectionsMessage(collectInvalidSectionLabels(currentStep, errors, touched))
      : null;

  const handleNext = () => {
    if (!currentStep) return;
    form.handleNext(values, currentStep, { setTouched, validateForm });
  };

  return (
    <Container maxW="container.xl" px={{ base: '16px', md: '24px' }} py={5}>
      <DashboardBackButton />
      <Box
        maxW="680px"
        mx="auto"
        w="100%"
        mt={3}
        bg="white"
        borderRadius="16px"
        boxShadow="md"
        overflow="hidden"
      >
        <Box px={{ base: 5, md: 8 }} pt={{ base: 4, md: 5 }} pb={3}>
          <MarketplaceConditionProgress
            steps={form.steps}
            currentStep={form.currentStep}
            isOnDone={form.isOnDone}
            timeEstimate={currentStep?.timeEstimate}
          />
        </Box>
        <Box borderTop="1px solid" borderColor="grayLightBackground" />
        <SlideFade key={form.isOnDone ? 'done' : form.currentStep} in offsetY="16px">
          <Box px={{ base: 5, md: 8 }} pt={{ base: 5, md: 7 }} pb={{ base: 6, md: 8 }}>
            {form.isOnDone ? (
              <ConditionReportDone steps={form.steps} onEdit={form.handleEditAnswers} />
            ) : currentStep ? (
              <StepShell title={currentStep.title} subtitle={currentStep.subtitle}>
                <StepQuestions step={currentStep} />
              </StepShell>
            ) : null}
          </Box>
        </SlideFade>
        <Box borderTop="1px solid" borderColor="grayLightBackground" />
        <Box px={{ base: 5, md: 8 }} py={4}>
          {form.isOnDone ? (
            <Flex justify="center">
              <Button
                variant="primary"
                onClick={form.handleBackToDashboard}
                w={{ base: '100%', md: '320px' }}
              >
                BACK TO DASHBOARD →
              </Button>
            </Flex>
          ) : currentStep ? (
            <Flex align="center" gap={3}>
              {form.currentStep > 0 && (
                <Text
                  as="button"
                  type="button"
                  fontSize="sm"
                  color="oceanBoatBlue"
                  fontWeight="600"
                  cursor="pointer"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={form.handleBack}
                >
                  ‹ Back
                </Text>
              )}
              <Button
                variant="primary"
                onClick={handleNext}
                isLoading={form.isSaving}
                isDisabled={stepHasUnansweredText(currentStep, values)}
                loadingText="Saving..."
                px={8}
                ml="auto"
              >
                {isLast ? 'FINISH CONDITION REPORT →' : 'NEXT →'}
              </Button>
            </Flex>
          ) : null}
          {missingSectionsMessage && (
            <Text mt={3} fontSize="sm" color="leaseEndRed" textAlign="center">
              {missingSectionsMessage}
            </Text>
          )}
        </Box>
      </Box>
    </Container>
  );
};

type MarketplaceConditionReportFormProps = {
  userDeal: UserDeal;
};

const MarketplaceConditionReportForm = ({ userDeal }: MarketplaceConditionReportFormProps) => {
  const dealId = userDeal?.id ?? '';
  const form = useMarketplaceConditionReport(dealId);
  const toast = useToast();

  useOnceWhen(form.isReady, () => trackConditionReportStarted(dealId));
  useOnceWhen(form.isOnDone, () => trackConditionReportCompleted(dealId));

  useEffect(() => {
    if (!form.loadError) return;
    toast({
      title: 'Could not load condition report',
      description: 'Sent you back to the dashboard.',
      status: 'error',
      duration: 4000,
      isClosable: true,
      position: 'top',
    });
  }, [form.loadError, toast]);

  if (form.loadError) return <Redirect to={DASHBOARD} />;

  if (!form.isReady) {
    return (
      <Container maxW="container.xl" my="20px">
        <Center py={20}>
          <Spinner color="oceanBoatBlue" />
        </Center>
      </Container>
    );
  }

  return (
    <Formik
      initialValues={form.initialValues}
      validationSchema={form.validationSchema}
      enableReinitialize
      onSubmit={() => undefined}
      validateOnChange={false}
      validateOnBlur
    >
      <Form>
        <FormContent form={form} />
      </Form>
    </Formik>
  );
};

export default MarketplaceConditionReportForm;
